'use client';

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { BetaColumnHeader } from './types';

export type StickyInfo = {
  side: 'left' | 'right';
  offset: number;
  isEdge: boolean;
};

type StickyProps = {
  style?: React.CSSProperties;
  'data-sticky'?: string;
  'data-sticky-edge'?: string;
};

export function buildStickyProps({
  info,
  canScrollLeft,
  canScrollRight,
}: {
  info: StickyInfo | undefined;
  canScrollLeft: boolean;
  canScrollRight: boolean;
}): StickyProps {
  if (!info) return {};
  const props: StickyProps = {
    style: { position: 'sticky', [info.side]: info.offset },
    'data-sticky': '',
  };
  if (info.isEdge && info.side === 'left' && canScrollLeft) {
    props['data-sticky-edge'] = 'left';
  }
  if (info.isEdge && info.side === 'right' && canScrollRight) {
    props['data-sticky-edge'] = 'right';
  }
  return props;
}

type StickyColumnsContextValue = {
  getStickyInfo: (absoluteColumnIndex: number) => StickyInfo | undefined;
  hasStickyColumns: boolean;
  hasStickyLeft: boolean;
  hasStickyRight: boolean;
  canScrollLeft: boolean;
  canScrollRight: boolean;
  checkboxOffset: number;
};

const defaultValue: StickyColumnsContextValue = {
  getStickyInfo: () => undefined,
  hasStickyColumns: false,
  hasStickyLeft: false,
  hasStickyRight: false,
  canScrollLeft: false,
  canScrollRight: false,
  checkboxOffset: 0,
};

export const StickyColumnsContext =
  createContext<StickyColumnsContextValue>(defaultValue);

export const useStickyColumnsContext = () => useContext(StickyColumnsContext);

const SCROLL_THRESHOLD = 1;

function getScrollState(container: Element) {
  const canScrollLeft = container.scrollLeft > SCROLL_THRESHOLD;
  const canScrollRight =
    container.scrollLeft + container.clientWidth <
    container.scrollWidth - SCROLL_THRESHOLD;
  return { canScrollLeft, canScrollRight };
}

function isScrollableElement(el: HTMLElement): boolean {
  if (el.scrollWidth <= el.clientWidth) return false;
  const overflow = getComputedStyle(el).overflowX;
  return overflow === 'auto' || overflow === 'scroll';
}

function findScrollableAncestor(start: HTMLElement): HTMLElement {
  let candidate: HTMLElement | null = start;
  while (
    candidate &&
    candidate !== document.body &&
    !isScrollableElement(candidate)
  ) {
    candidate = candidate.parentElement;
  }
  return !candidate || candidate === document.body ? start : candidate;
}

export function useStickyColumns({
  columns,
  showCheckboxes,
  table,
  scrollContainer,
}: {
  columns: BetaColumnHeader[];
  showCheckboxes: boolean;
  table: HTMLTableElement | null;
  scrollContainer: HTMLElement | null;
}): StickyColumnsContextValue {
  // Each sticky column needs a pixel offset (left or right) that accounts for
  // the measured widths of all preceding sticky columns on the same side.
  // A simple "can scroll" boolean isn't enough — CSS position: sticky requires
  // explicit left/right values per cell, so we build a map of column-index →
  // { side, offset, isEdge } and rebuild it via ResizeObserver when widths change.
  const [stickyMap, setStickyMap] = useState<Map<number, StickyInfo>>(
    new Map()
  );
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const hasStickyColumns = columns.some((c) => c.isSticky);

  const config = useMemo(() => {
    if (!hasStickyColumns) return null;

    const checkboxOffset = showCheckboxes ? 1 : 0;

    let leftStickyCount = 0;
    for (const col of columns) {
      if (col.isSticky) leftStickyCount += 1;
      else break;
    }

    let rightStickyCount = 0;
    for (const col of [...columns].reverse()) {
      if (col.isSticky) rightStickyCount += 1;
      else break;
    }
    rightStickyCount = Math.min(
      rightStickyCount,
      columns.length - leftStickyCount
    );

    if (process.env.NODE_ENV !== 'production') {
      const totalSticky = columns.filter((c) => c.isSticky).length;
      if (totalSticky !== leftStickyCount + rightStickyCount) {
        console.warn(
          'DataTable: Sticky columns must be contiguous from the start or end of the table. ' +
            'Columns marked as sticky in the middle will be ignored.'
        );
      }
    }

    return {
      checkboxOffset,
      leftStickyCount,
      rightStickyCount,
      checkboxIsSticky: showCheckboxes && leftStickyCount > 0,
    };
  }, [columns, showCheckboxes, hasStickyColumns]);

  useEffect(() => {
    if (!config || !table) return;

    const measure = () => {
      const headerRow = table.querySelector('thead tr');
      if (!headerRow) return;

      const cells = Array.from(headerRow.children) as HTMLElement[];
      const newMap = new Map<number, StickyInfo>();
      const {
        checkboxOffset,
        leftStickyCount,
        rightStickyCount,
        checkboxIsSticky,
      } = config;

      const cellWidth = (cell: HTMLElement) =>
        cell.getBoundingClientRect().width;

      if (checkboxIsSticky && cells[0]) {
        newMap.set(0, {
          side: 'left',
          offset: 0,
          // Checkbox is never the edge — checkboxIsSticky requires leftStickyCount > 0,
          // so the last left-sticky data column is always the edge.
          isEdge: false,
        });
      }

      let leftOffset = checkboxIsSticky && cells[0] ? cellWidth(cells[0]) : 0;
      for (let i = 0; i < leftStickyCount; i += 1) {
        const absIdx = i + checkboxOffset;
        newMap.set(absIdx, {
          side: 'left',
          offset: leftOffset,
          isEdge: i === leftStickyCount - 1,
        });
        if (cells[absIdx]) leftOffset += cellWidth(cells[absIdx]);
      }

      let rightOffset = 0;
      for (let i = rightStickyCount - 1; i >= 0; i -= 1) {
        const colIdx = columns.length - rightStickyCount + i;
        const absIdx = colIdx + checkboxOffset;
        newMap.set(absIdx, {
          side: 'right',
          offset: rightOffset,
          isEdge: i === 0,
        });
        if (cells[absIdx]) rightOffset += cellWidth(cells[absIdx]);
      }

      setStickyMap(newMap);
    };

    measure();

    const observer = new ResizeObserver(measure);
    // Observe the table itself so measurement retries when children mount.
    // Individual header cells are also observed for column-resize precision.
    observer.observe(table);
    const headerRow = table.querySelector('thead tr');
    if (headerRow) {
      Array.from(headerRow.children).forEach((cell) => observer.observe(cell));
    }

    return () => observer.disconnect();
  }, [config, table, columns.length]);

  useEffect(() => {
    if (!scrollContainer) return;

    // Track the current scrollable ancestor so we can re-attach the scroll
    // listener if the overflow state of the DOM changes after mount (e.g. the
    // table becomes wider than its container after data loads).
    const scrollerRef = { current: findScrollableAncestor(scrollContainer) };

    const updateScrollState = () => {
      const resolved = findScrollableAncestor(scrollContainer);
      if (resolved !== scrollerRef.current) {
        scrollerRef.current.removeEventListener('scroll', updateScrollState);
        scrollerRef.current = resolved;
        scrollerRef.current.addEventListener('scroll', updateScrollState, {
          passive: true,
        });
      }
      const state = getScrollState(scrollerRef.current);
      setCanScrollLeft(state.canScrollLeft);
      setCanScrollRight(state.canScrollRight);
    };

    updateScrollState();

    scrollerRef.current.addEventListener('scroll', updateScrollState, {
      passive: true,
    });

    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(scrollContainer);
    if (table) resizeObserver.observe(table);

    return () => {
      scrollerRef.current.removeEventListener('scroll', updateScrollState);
      resizeObserver.disconnect();
    };
  }, [scrollContainer, table]);

  const checkboxOffset = showCheckboxes ? 1 : 0;

  const hasStickyLeft = config ? config.leftStickyCount > 0 : false;
  const hasStickyRight = config ? config.rightStickyCount > 0 : false;

  return useMemo(
    () => ({
      getStickyInfo: (absoluteColumnIndex: number) =>
        stickyMap.get(absoluteColumnIndex),
      hasStickyColumns,
      hasStickyLeft,
      hasStickyRight,
      canScrollLeft,
      canScrollRight,
      checkboxOffset,
    }),
    [
      stickyMap,
      hasStickyColumns,
      hasStickyLeft,
      hasStickyRight,
      canScrollLeft,
      canScrollRight,
      checkboxOffset,
    ]
  );
}
